import { normalize, normalizeSection } from './recipes';
import { getPantryKey, pantryStateToItems } from './pantryStorage';

export function toPriceNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function formatDkk(value) {
  const amount = toPriceNumber(value);
  if (amount === null) {
    return '';
  }
  return `${Math.round(amount)} kr.`;
}

export function toBool(value, fallback = false) {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['ja', 'yes', 'true', '1'].includes(normalized)) {
      return true;
    }
    if (['nej', 'no', 'false', '0'].includes(normalized)) {
      return false;
    }
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return fallback;
}

export function getRecipeIngredients(recipe) {
  if (Array.isArray(recipe?.ingredients)) {
    return recipe.ingredients;
  }
  if (Array.isArray(recipe?.ingredienser)) {
    return recipe.ingredienser;
  }
  return [];
}

export function isShoppingIngredient(ingredient) {
  return toBool(
    ingredient?.in_shopping_basket,
    toBool(ingredient?.required_in_base) &&
      !toBool(ingredient?.pantry_basic, normalizeSection(ingredient?.sektion) === 'basis')
  );
}

export function buildPriceLineMap(pricingSource) {
  const lineItems = Array.isArray(pricingSource?.public_price?.line_items)
    ? pricingSource.public_price.line_items
    : [];

  return new Map(
    lineItems
      .map((line) => [normalize(line.produkt_navn).toLocaleLowerCase('da-DK'), line])
      .filter(([key]) => key)
  );
}

export function ingredientMatchesPantryItem(ingredient, pantryItem) {
  const normalizeMatchKey = (value) => getPantryKey(value)
    .replace(/^(?:økologisk(?:e)?|dansk(?:e)?|frisk(?:e)?|gul(?:e)?|rød(?:e)?)\s+/i, '')
    .replace(/\s+(?:i pose|stor pose|kasseklar|familiepakke)$/i, '')
    .trim();
  const pantryKey = normalizeMatchKey(pantryItem?.productName || pantryItem?.key);
  if (!pantryKey) {
    return false;
  }

  const allowedVariants = typeof ingredient?.allowed_variants === 'string'
    ? ingredient.allowed_variants.split('|')
    : Array.isArray(ingredient?.allowed_variants)
      ? ingredient.allowed_variants
      : [];
  const names = [
    ingredient?.produkt_navn,
    ingredient?.selected_product_name,
    ingredient?.ingredient_family_name,
    ...allowedVariants,
  ];
  const ingredientKeys = names.map(normalizeMatchKey).filter(Boolean);
  const conflictingSpecificProducts = [
    { generic: 'kartofler', specific: 'søde kartofler' },
    { generic: 'løg', specific: 'hvidløg' },
    { generic: 'mælk', specific: 'kokosmælk' },
  ];
  const hasIdentityConflict = conflictingSpecificProducts.some(({ generic, specific }) => {
    const ingredientAllowsSpecific = ingredientKeys.some((key) => key.includes(specific));
    const ingredientUsesGeneric = ingredientKeys.some((key) => key === generic);
    return (
      pantryKey.includes(specific) && ingredientUsesGeneric && !ingredientAllowsSpecific
    ) || (
      pantryKey === generic && ingredientAllowsSpecific && !ingredientUsesGeneric
    );
  });
  if (hasIdentityConflict) {
    return false;
  }

  return ingredientKeys.some((key) => (
    key === pantryKey || key.includes(pantryKey) || pantryKey.includes(key)
  ));
}

const UNIT_CONVERSIONS = {
  mg: { kind: 'mass', factor: 0.001 },
  g: { kind: 'mass', factor: 1 },
  kg: { kind: 'mass', factor: 1000 },
  ml: { kind: 'volume', factor: 1 },
  cl: { kind: 'volume', factor: 10 },
  dl: { kind: 'volume', factor: 100 },
  l: { kind: 'volume', factor: 1000 },
  tsk: { kind: 'volume', factor: 5 },
  spsk: { kind: 'volume', factor: 15 },
  stk: { kind: 'count', factor: 1 },
  fed: { kind: 'clove', factor: 1 },
};

function toQuantityNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const number = Number(typeof value === 'string' ? value.replace(',', '.') : value);
  return Number.isFinite(number) ? number : null;
}

export function getRequiredIngredientQuantity(ingredient, priceLine) {
  const adaptedAmount = toQuantityNumber(priceLine?.adapted_brug_maengde);
  const adaptedUnit = normalize(priceLine?.adapted_brug_enhed).toLocaleLowerCase('da-DK');
  if (adaptedAmount !== null && adaptedUnit) {
    return { amount: adaptedAmount, unit: adaptedUnit };
  }

  const usageAmount = toQuantityNumber(ingredient?.brug_maengde);
  const usageUnit = normalize(ingredient?.brug_enhed).toLocaleLowerCase('da-DK');
  if (usageAmount !== null && usageUnit) {
    return { amount: usageAmount, unit: usageUnit };
  }

  const usesWholePackage = toBool(ingredient?.brug_er_hele_pakken);
  const packageSize = toQuantityNumber(ingredient?.pakke_stoerrelse);
  const packageCount = toQuantityNumber(ingredient?.antal_pakker) || 1;
  const packageUnit = normalize(ingredient?.pakke_enhed).toLocaleLowerCase('da-DK');
  if (usesWholePackage && packageSize !== null && packageUnit) {
    return { amount: packageCount * packageSize, unit: packageUnit };
  }

  return { amount: null, unit: '' };
}

export function getPantryItemQuantity(pantryItem) {
  const quantityAmount = toQuantityNumber(pantryItem?.quantityAmount);
  const quantityUnit = normalize(pantryItem?.quantityUnit).toLocaleLowerCase('da-DK');
  if (quantityAmount !== null && quantityUnit) {
    return { amount: quantityAmount, unit: quantityUnit };
  }

  const packageCount = toQuantityNumber(pantryItem?.packageCount);
  const packageSize = toQuantityNumber(pantryItem?.packageSize);
  const packageUnit = normalize(pantryItem?.packageUnit).toLocaleLowerCase('da-DK');
  if (packageCount !== null && packageSize !== null && packageUnit) {
    return { amount: packageCount * packageSize, unit: packageUnit };
  }

  return { amount: null, unit: '' };
}

export function convertQuantityAmount(amount, fromUnit, toUnit) {
  const numericAmount = toQuantityNumber(amount);
  const normalizedFromUnit = normalize(fromUnit).toLocaleLowerCase('da-DK');
  const normalizedToUnit = normalize(toUnit).toLocaleLowerCase('da-DK');
  if (numericAmount === null || !normalizedFromUnit || !normalizedToUnit) {
    return null;
  }
  if (normalizedFromUnit === normalizedToUnit) {
    return numericAmount;
  }

  const fromConversion = UNIT_CONVERSIONS[normalizedFromUnit];
  const toConversion = UNIT_CONVERSIONS[normalizedToUnit];
  if (!fromConversion || !toConversion || fromConversion.kind !== toConversion.kind) {
    return null;
  }

  return (numericAmount * fromConversion.factor) / toConversion.factor;
}

const PIECE_WEIGHT_RULES = [
  { pattern: /spidskål/, grams: 900 },
  { pattern: /hvidkål/, grams: 1000 },
  { pattern: /blomkål/, grams: 600 },
  { pattern: /rødløg/, grams: 110 },
  { pattern: /(?:^|\s)løg(?:\s|$)/, grams: 100 },
  { pattern: /guler(?:od|odder|[oø]dder)/, grams: 80 },
  { pattern: /kartoffel|kartofler/, grams: 125 },
  { pattern: /peberfrugt/, grams: 150 },
];

const getIngredientPieceWeight = (ingredient) => {
  const allowedVariants = typeof ingredient?.allowed_variants === 'string'
    ? ingredient.allowed_variants.split('|')
    : Array.isArray(ingredient?.allowed_variants)
      ? ingredient.allowed_variants
      : [];
  const identity = [
    ingredient?.produkt_navn,
    ingredient?.ingredient_family_name,
    ...allowedVariants,
  ]
    .map((value) => getPantryKey(value))
    .filter(Boolean)
    .join(' ');
  const rule = PIECE_WEIGHT_RULES.find(({ pattern }) => pattern.test(identity));
  return rule?.grams || null;
};

export function convertIngredientQuantityAmount(amount, fromUnit, toUnit, ingredient) {
  const directAmount = convertQuantityAmount(amount, fromUnit, toUnit);
  if (directAmount !== null) {
    return directAmount;
  }

  const normalizedFromUnit = normalize(fromUnit).toLocaleLowerCase('da-DK');
  const normalizedToUnit = normalize(toUnit).toLocaleLowerCase('da-DK');
  const gramsPerPiece = getIngredientPieceWeight(ingredient);
  if (!gramsPerPiece) {
    return null;
  }

  if (normalizedFromUnit === 'stk' && ['mg', 'g', 'kg'].includes(normalizedToUnit)) {
    return convertQuantityAmount(toQuantityNumber(amount) * gramsPerPiece, 'g', normalizedToUnit);
  }
  if (['mg', 'g', 'kg'].includes(normalizedFromUnit) && normalizedToUnit === 'stk') {
    const grams = convertQuantityAmount(amount, normalizedFromUnit, 'g');
    return grams === null ? null : grams / gramsPerPiece;
  }

  return null;
}

function quantityCoversRequirement(available, required, ingredient) {
  if (required.amount === null || !required.unit) {
    return true;
  }
  if (available.amount === null || !available.unit) {
    return false;
  }

  const convertedAmount = convertIngredientQuantityAmount(
    available.amount,
    available.unit,
    required.unit,
    ingredient
  );
  return convertedAmount !== null && convertedAmount >= required.amount;
}

export function pantryItemCoversIngredient(ingredient, pantryItem, priceLine = null) {
  if (!ingredientMatchesPantryItem(ingredient, pantryItem)) {
    return false;
  }

  return quantityCoversRequirement(
    getPantryItemQuantity(pantryItem),
    getRequiredIngredientQuantity(ingredient, priceLine),
    ingredient
  );
}

export function getPantryItemsFromValue(value) {
  if (Array.isArray(value)) {
    return value;
  }
  return pantryStateToItems(value);
}

export function buildRecipePriceComparison(recipe, options = {}) {
  const pricingSource = options.pricingSource || recipe;
  const pantryItems = getPantryItemsFromValue(options.pantryItems || options.pantryState || {});
  const shoppingIngredients = getRecipeIngredients(recipe).filter(isShoppingIngredient);
  const priceLineByProduct = buildPriceLineMap(pricingSource);
  const getPriceLine = (ingredient) => priceLineByProduct.get(
    normalize(ingredient?.produkt_navn).toLocaleLowerCase('da-DK')
  );
  const matchedIngredients = shoppingIngredients.filter((ingredient) => pantryItems.some((item) =>
    pantryItemCoversIngredient(ingredient, item, getPriceLine(ingredient))
  ));
  const matchedItems = pantryItems.filter((item) =>
    shoppingIngredients.some((ingredient) =>
      pantryItemCoversIngredient(ingredient, item, getPriceLine(ingredient))
    )
  );
  const pricingRows = shoppingIngredients.map((ingredient) => {
    const productKey = normalize(ingredient.produkt_navn).toLocaleLowerCase('da-DK');
    const priceLine = priceLineByProduct.get(productKey);
    return {
      ingredient,
      priceLine,
      weeklyValue: toPriceNumber(priceLine?.pris_denne_uge_dkk),
      isMatched: matchedIngredients.includes(ingredient),
    };
  });
  const hasCompleteWeeklyPricing =
    pricingRows.length > 0 && pricingRows.every((row) => row.weeklyValue !== null);
  const publicWeeklyValue = toPriceNumber(pricingSource?.public_price?.pris_denne_uge_dkk);
  const fullWeeklyValue = hasCompleteWeeklyPricing
    ? pricingRows.reduce((sum, row) => sum + row.weeklyValue, 0)
    : publicWeeklyValue;
  const matchedWeeklyValue = pricingRows.reduce((sum, row) => (
    row.isMatched && row.weeklyValue !== null ? sum + row.weeklyValue : sum
  ), 0);
  const pantryWeeklyValue =
    fullWeeklyValue !== null && matchedWeeklyValue > 0
      ? Math.max(fullWeeklyValue - matchedWeeklyValue, 0)
      : null;
  const pantrySavingsValue =
    fullWeeklyValue !== null && pantryWeeklyValue !== null
      ? Math.max(fullWeeklyValue - pantryWeeklyValue, 0)
      : null;
  const hasCompletePantryCoverage =
    shoppingIngredients.length > 0 && matchedIngredients.length === shoppingIngredients.length;

  return {
    fullWeeklyValue,
    pantryWeeklyValue,
    pantrySavingsValue,
    fullWeeklyLabel: formatDkk(fullWeeklyValue),
    pantryWeeklyLabel: hasCompletePantryCoverage ? 'Alt er d\u00e6kket' : formatDkk(pantryWeeklyValue),
    matchedIngredients,
    matchedItems,
    matchedCount: matchedItems.length,
    hasCompleteWeeklyPricing,
    hasFullWeeklyPrice: fullWeeklyValue !== null,
    hasPantryWeeklyPrice: pantryWeeklyValue !== null,
    hasPantrySavings: pantrySavingsValue !== null && pantrySavingsValue > 0,
    hasCompletePantryCoverage,
  };
}
